import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client.ts";
import { confirmAction, toast } from "./feedback.tsx";
import { Card } from "./ui.tsx";
import { useUnsavedChanges } from "./unsaved.ts";

interface VariantRow {
  id: string;
  step_index: number | null;
  label: string;
  template_id: string | null;
  template_name: string | null;
  subject: string | null;
  preview_text: string | null;
  weight: number;
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
  unsubscribed: number;
}

interface Arm {
  label: string;
  templateId: string;
  subject: string;
  previewText: string;
  weight: number;
}

interface TemplateOption {
  id: string;
  name: string;
  brandId: string;
}

/** What is being tested: the broadcast, or one step of a sequence. */
export interface TestTarget {
  stepIndex: number | null;
  title: string;
  /** What a version inherits when it leaves a field blank. */
  baseSubject: string | null;
  baseTemplate: string | null;
}

const LETTERS = ["A", "B", "C", "D", "E"];

const blankArm = (label: string): Arm => ({
  label,
  templateId: "",
  subject: "",
  previewText: "",
  weight: 1,
});

/**
 * A/B tests for a campaign: one for a broadcast, one per step for a sequence.
 * Each contact always gets the same version (and the same letter on every
 * step), so results compare like with like. Replies are the number to judge
 * by; opens are inflated by privacy proxies. There is no automatic winner —
 * at these volumes one would crown a version on 3 replies to 1.
 */
export function AbTests({
  campaignId,
  brandId,
  targets,
}: {
  campaignId: string;
  brandId: string;
  targets: TestTarget[];
}) {
  const variants = useQuery({
    queryKey: ["variants", campaignId],
    queryFn: () => api.get<VariantRow[]>(`/campaigns/${campaignId}/variants`),
  });
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<TemplateOption[]>("/templates"),
  });
  const brandTemplates = (templates.data ?? []).filter((t) => t.brandId === brandId);
  if (!variants.data) return null;

  return (
    <Card
      title="A/B tests"
      subtitle="Each contact always gets the same version. Judge by replies; opens are inflated by privacy proxies."
    >
      {targets.map((target) => (
        <TestBlock
          key={target.stepIndex ?? "broadcast"}
          campaignId={campaignId}
          target={target}
          arms={variants.data.filter((v) => v.step_index === target.stepIndex)}
          templates={brandTemplates}
        />
      ))}
    </Card>
  );
}

function TestBlock({
  campaignId,
  target,
  arms,
  templates,
}: {
  campaignId: string;
  target: TestTarget;
  arms: VariantRow[];
  templates: TemplateOption[];
}) {
  const started = arms.some((a) => a.sent > 0);
  const [editing, setEditing] = useState(false);

  return (
    <div className="ab-block">
      <div className="ab-head">
        <strong>{target.title}</strong>
        {arms.length === 0 && !editing && (
          <button type="button" className="btn ghost small" onClick={() => setEditing(true)}>
            + Test this
          </button>
        )}
        {arms.length > 0 && !started && !editing && (
          <button type="button" className="btn ghost small" onClick={() => setEditing(true)}>
            Edit test
          </button>
        )}
      </div>
      {editing ? (
        <ArmEditor
          campaignId={campaignId}
          target={target}
          existing={arms}
          templates={templates}
          onDone={() => setEditing(false)}
        />
      ) : arms.length > 0 ? (
        <Results campaignId={campaignId} arms={arms} target={target} />
      ) : (
        <p className="hint">Not tested: everyone gets the same email.</p>
      )}
    </div>
  );
}

function ArmEditor({
  campaignId,
  target,
  existing,
  templates,
  onDone,
}: {
  campaignId: string;
  target: TestTarget;
  existing: VariantRow[];
  templates: TemplateOption[];
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const initial: Arm[] =
    existing.length > 0
      ? existing.map((v) => ({
          label: v.label,
          templateId: v.template_id ?? "",
          subject: v.subject ?? "",
          previewText: v.preview_text ?? "",
          weight: v.weight,
        }))
      : [blankArm("A"), blankArm("B")];
  const [arms, setArms] = useState<Arm[]>(initial);
  useUnsavedChanges(JSON.stringify(arms) !== JSON.stringify(initial));

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["variants", campaignId] });
  const save = useMutation({
    mutationFn: () =>
      api.put<{ warnings?: string[] }>(`/campaigns/${campaignId}/variants`, {
        stepIndex: target.stepIndex,
        arms: arms.map((a) => ({
          label: a.label,
          templateId: a.templateId || null,
          subject: a.subject || null,
          previewText: a.previewText || null,
          weight: a.weight,
        })),
      }),
    onSuccess: (res: { warnings?: string[] }) => {
      toast("A/B test saved");
      for (const w of res.warnings ?? []) toast(w, "bad");
      invalidate();
      onDone();
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
  const remove = useMutation({
    mutationFn: () =>
      api.del(
        `/campaigns/${campaignId}/variants?step=${target.stepIndex === null ? "" : target.stepIndex}`,
      ),
    onSuccess: () => {
      toast("A/B test removed");
      invalidate();
      onDone();
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });

  const set = (i: number, patch: Partial<Arm>) =>
    setArms(arms.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const total = arms.reduce((s, a) => s + a.weight, 0) || 1;
  // A version that changes nothing is the control; more than one is a mistake.
  const controls = arms.filter((a) => !a.templateId && !a.subject && !a.previewText).length;

  return (
    <div className="ab-editor">
      {arms.map((arm, i) => (
        <div className="ab-arm" key={arm.label}>
          <div className="ab-arm-label">
            <span className="ab-letter">{arm.label}</span>
            <span className="hint">{Math.round((arm.weight / total) * 100)}%</span>
          </div>
          <div className="ab-arm-fields">
            <input
              aria-label={`Version ${arm.label} subject`}
              placeholder={`Subject: ${target.baseSubject ?? "the template's own"}`}
              value={arm.subject}
              onChange={(e) => set(i, { subject: e.target.value })}
            />
            <input
              aria-label={`Version ${arm.label} preview text`}
              placeholder="Preview line: the template's own"
              value={arm.previewText}
              onChange={(e) => set(i, { previewText: e.target.value })}
            />
            <select
              aria-label={`Version ${arm.label} template`}
              value={arm.templateId}
              onChange={(e) => set(i, { templateId: e.target.value })}
            >
              <option value="">Template: {target.baseTemplate ?? "the campaign's own"}</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  Template: {t.name}
                </option>
              ))}
            </select>
            <select
              aria-label={`Version ${arm.label} share`}
              value={arm.weight}
              onChange={(e) => set(i, { weight: Number(e.target.value) })}
            >
              {[1, 2, 3, 4].map((w) => (
                <option key={w} value={w}>
                  Share ×{w}
                </option>
              ))}
            </select>
          </div>
          {arms.length > 2 && (
            <button
              type="button"
              className="btn ghost small"
              onClick={() => setArms(arms.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      <p className="hint">
        Blank fields keep the original. Use merge fields as in the template, e.g. {"{{first_name}}"}
        .{controls > 1 && " Two versions change nothing — they're the same email."}
      </p>
      <div className="card-actions">
        {arms.length < LETTERS.length && (
          <button
            type="button"
            className="btn ghost small"
            onClick={() =>
              setArms([
                ...arms,
                blankArm(LETTERS.find((l) => !arms.some((a) => a.label === l)) ?? "E"),
              ])
            }
          >
            + Add version
          </button>
        )}
        <span className="spacer" />
        {existing.length > 0 && (
          <button
            type="button"
            className="btn ghost small"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Remove test
          </button>
        )}
        <button type="button" className="btn ghost small" onClick={onDone}>
          Cancel
        </button>
        <button
          type="button"
          className="btn small"
          disabled={save.isPending || controls === arms.length}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save test"}
        </button>
      </div>
    </div>
  );
}

const rate = (n: number, of: number) => (of ? `${Math.round((n / of) * 1000) / 10}%` : "—");

function Results({
  campaignId,
  arms,
  target,
}: {
  campaignId: string;
  arms: VariantRow[];
  target: TestTarget;
}) {
  const qc = useQueryClient();
  const live = arms.filter((a) => a.weight > 0);
  const decided = live.length === 1 && arms.length > 1;
  const winner = useMutation({
    mutationFn: (variantId: string) =>
      api.post(`/campaigns/${campaignId}/variants/${variantId}/winner`),
    onSuccess: () => {
      toast("Everyone from now gets that version");
      void qc.invalidateQueries({ queryKey: ["variants", campaignId] });
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
  const pick = async (arm: VariantRow) => {
    if (
      await confirmAction({
        title: `Send version ${arm.label} to everyone from now?`,
        message:
          "The other versions stop going out. What they've sent so far stays on this report.",
        confirmLabel: `Use version ${arm.label}`,
      })
    )
      winner.mutate(arm.id);
  };
  const changed = (a: VariantRow) =>
    [
      a.subject && `Subject: ${a.subject}`,
      a.preview_text && `Preview: ${a.preview_text}`,
      a.template_name && `Template: ${a.template_name}`,
    ]
      .filter(Boolean)
      .join(" · ") || `Original${target.baseSubject ? ` (${target.baseSubject})` : ""}`;

  return (
    <table className="ab-results">
      <thead>
        <tr>
          <th>Version</th>
          <th className="num">Sent</th>
          <th className="num">Replied</th>
          <th className="num">Opened</th>
          <th className="num">Clicked</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {arms.map((a) => (
          <tr key={a.id} style={a.weight === 0 ? { color: "var(--gray-500)" } : undefined}>
            <td>
              <span className="ab-letter">{a.label}</span> {changed(a)}
              {decided && a.weight > 0 && <span className="pill ok"> Winner</span>}
            </td>
            <td className="num">{a.sent.toLocaleString()}</td>
            <td className="num" style={{ fontWeight: 700 }}>
              {rate(a.replied, a.sent)}
              <span className="rate-n">{a.replied.toLocaleString()}</span>
            </td>
            <td className="num">
              {rate(a.opened, a.sent)}
              <span className="rate-n">{a.opened.toLocaleString()}</span>
            </td>
            <td className="num">
              {rate(a.clicked, a.sent)}
              <span className="rate-n">{a.clicked.toLocaleString()}</span>
            </td>
            <td style={{ textAlign: "right" }}>
              {!decided && a.weight > 0 && a.sent > 0 && (
                <button
                  type="button"
                  className="btn ghost small"
                  disabled={winner.isPending}
                  onClick={() => pick(a)}
                >
                  Use for everyone
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
